'use client';

import { createContext, useContext, useMemo } from 'react';

import type { Capability } from '../index';

const CapabilitiesContext = createContext<ReadonlySet<Capability> | null>(null);

interface CapabilitiesProviderProps {
  /** Resolved capability keys for the current tenant. Must come from the server. */
  capabilities: readonly Capability[];
  children: React.ReactNode;
}

export function CapabilitiesProvider({ capabilities, children }: CapabilitiesProviderProps) {
  const set = useMemo(() => new Set(capabilities), [capabilities]);
  return <CapabilitiesContext.Provider value={set}>{children}</CapabilitiesContext.Provider>;
}

/** Returns true if the capability is enabled for the current tenant. */
export function useCapability(key: Capability): boolean {
  const ctx = useContext(CapabilitiesContext);
  if (ctx === null) {
    throw new Error('useCapability must be used within a CapabilitiesProvider');
  }
  return ctx.has(key);
}

/**
 * Same as `useCapability` but returns `false` when no provider is mounted.
 * Useful for shared components rendered both inside and outside the tenant tree.
 */
export function useCapabilityOptional(key: Capability): boolean {
  const ctx = useContext(CapabilitiesContext);
  return ctx?.has(key) ?? false;
}

/** The full enabled-capability set for the current tenant. */
export function useCapabilities(): ReadonlySet<Capability> {
  const ctx = useContext(CapabilitiesContext);
  if (ctx === null) {
    throw new Error('useCapabilities must be used within a CapabilitiesProvider');
  }
  return ctx;
}
